import type { NetworkGateway } from '../application/networkGateway';
import type { AccessResult } from '../domain/access';
import type {
  NetworkCollection,
  NetworkCollectionItem,
  NetworkCollectionItemsResult,
  NetworkCollectionKind,
  NetworkCollectionsResult,
  NetworkConnection,
  NetworkConnectionsResult,
  NetworkCountsResult,
  NetworkMatchRequestResult,
  NetworkMutationResult,
  NetworkPageResult,
  NetworkProfessional,
  NetworkProfessionalProfile,
  NetworkProfessionalProfileResult,
  NetworkProperty,
  NetworkPropertyPageResult,
  NetworkPropertyRequest,
  NetworkRelationshipCounts,
  NetworkRequest,
} from '../domain/network';
import type {
  ConversationMessage,
  ConversationMessagesResult,
  SendMessageResult,
} from '../domain/workspaces';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession {
  readonly accessToken: string;
  readonly expiresAt: number;
}

interface ProfessionalRow {
  readonly agent_id: string;
  readonly public_name: string;
  readonly headline: string | null;
  readonly bio: string | null;
  readonly city: string;
  readonly state: string;
  readonly specialties: string[];
  readonly service_areas: string[];
  readonly agency_name: string | null;
  readonly professional_role: string | null;
  readonly accepts_matches: boolean;
  readonly is_seen: boolean;
  readonly outgoing_match_status: string | null;
  readonly incoming_match_status: string | null;
  readonly has_active_match: boolean;
  readonly is_self: boolean;
  readonly total_count: number | string;
}

interface PropertyRow {
  readonly listing_id: string;
  readonly property_id: string;
  readonly title: string;
  readonly description: string;
  readonly transaction_type: 'sale' | 'rent';
  readonly price: number | string;
  readonly currency: string;
  readonly city: string;
  readonly state: string;
  readonly primary_agent_id: string | null;
  readonly primary_agent_name: string | null;
  readonly agency_name: string;
  readonly media_path: string | null;
  readonly media_count: number | string;
  readonly is_favorite: boolean;
  readonly outgoing_match_status: string | null;
  readonly incoming_match_status: string | null;
  readonly has_active_match: boolean;
  readonly total_count: number | string;
}

interface ProfessionalProfileRow {
  readonly agent_id: string;
  readonly public_name: string;
  readonly license_number: string | null;
  readonly city: string;
  readonly state: string;
  readonly specialties: string[];
  readonly headline: string | null;
  readonly bio: string | null;
  readonly service_areas: string[];
  readonly agency_name: string | null;
  readonly professional_role: string | null;
  readonly inventory: {
    listing_id: string;
    title: string;
    transaction_type: 'sale' | 'rent';
    price: number | string;
    currency: string;
    property_type: string;
    city: string;
    state: string;
  }[];
  readonly blog_posts: {
    post_id: string;
    title: string;
    subtitle: string | null;
    body: string;
    published_at: string | null;
  }[];
}

interface CollectionItemRow {
  readonly item_type: 'professional' | 'property';
  readonly item_id: string;
  readonly agent_id: string | null;
  readonly listing_id: string | null;
  readonly title: string;
  readonly subtitle: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly agency_name: string | null;
  readonly relationship_label: string;
  readonly media_path: string | null;
}

interface CollectionRow {
  readonly collection_id: string;
  readonly name: string;
  readonly visibility: string;
  readonly workspace_id: string | null;
  readonly item_count: number | string;
}

interface CountsRow {
  readonly customers: number | string;
  readonly owners: number | string;
  readonly active_matches: number | string;
  readonly pending_outgoing: number | string;
  readonly visible_dms: number | string;
}

interface ConnectionRow {
  readonly match_id: string;
  readonly agent_id: string;
  readonly public_name: string;
  readonly headline: string | null;
  readonly city: string;
  readonly state: string;
  readonly specialties: string[];
  readonly agency_name: string | null;
  readonly professional_role: string | null;
  readonly conversation_id: string | null;
  readonly matched_at: string;
}

interface MatchStateRow {
  readonly status: 'pending' | 'matched';
  readonly conversation_id?: string | null;
}

interface MessageRow {
  readonly message_id: string;
  readonly body: string;
  readonly message_type: string;
  readonly created_at: string;
  readonly sender_agent_id: string;
  readonly sender_name: string;
  readonly is_mine: boolean;
}

function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;

    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Math.floor(Date.now() / 1000)
    ) {
      return null;
    }

    return parsed.accessToken;
  } catch {
    return null;
  }
}

export class SupabaseNetworkGateway implements NetworkGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> {
    return this.accessGateway.resolveCurrentIdentity(signal);
  }

  signOut(signal?: AbortSignal): Promise<void> {
    return this.accessGateway.signOut(signal);
  }

  private async rpc<T>(
    functionName: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();

    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return null;
    }

    const response = await fetch(
      `${supabaseUrl}/rest/v1/rpc/${functionName}`,
      {
        method: 'POST',
        headers: {
          apikey: publishableKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
        ...(signal === undefined ? {} : { signal }),
      },
    );

    if (!response.ok) return null;
    return (await response.json()) as T;
  }

  async loadNetwork(
    request: NetworkRequest,
    signal?: AbortSignal,
  ): Promise<NetworkPageResult> {
    try {
      const rows = await this.rpc<ProfessionalRow[]>(
        'network_directory_v2',
        {
          p_query: request.query.trim() === '' ? null : request.query.trim(),
          p_near_me: request.nearMe,
          p_seen_only: request.seenOnly,
          p_cursor_name: request.cursor?.publicName ?? null,
          p_cursor_agent_id: request.cursor?.agentId ?? null,
          p_limit: request.limit,
        },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer Network.' };
      }

      const professionals: NetworkProfessional[] = rows.map((row) => ({
        agentId: row.agent_id,
        publicName: row.public_name,
        headline: row.headline,
        bio: row.bio,
        city: row.city,
        state: row.state,
        specialties: row.specialties,
        serviceAreas: row.service_areas,
        agencyName: row.agency_name,
        professionalRole: row.professional_role,
        acceptsMatches: row.accepts_matches,
        isSeen: row.is_seen,
        outgoingMatchStatus: row.outgoing_match_status,
        incomingMatchStatus: row.incoming_match_status,
        hasActiveMatch: row.has_active_match,
        isSelf: row.is_self,
      }));

      const last = professionals.at(-1) ?? null;
      const totalCount =
        rows.length === 0 ? 0 : Number(rows[0]?.total_count ?? 0);

      return {
        ok: true,
        page: {
          professionals,
          totalCount,
          nextCursor:
            rows.length === request.limit && last !== null
              ? { publicName: last.publicName, agentId: last.agentId }
              : null,
        },
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible conectar con Network.' };
    }
  }

  async loadPropertyFeed(
    request: NetworkPropertyRequest,
    signal?: AbortSignal,
  ): Promise<NetworkPropertyPageResult> {
    try {
      const rows = await this.rpc<PropertyRow[]>(
        'network_property_feed_v1',
        {
          p_query: request.query.trim() === '' ? null : request.query.trim(),
          p_near_me: request.nearMe,
          p_offset: request.offset,
          p_limit: request.limit,
        },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer Propiedades.' };
      }

      const properties: NetworkProperty[] = rows.map((row) => ({
        listingId: row.listing_id,
        propertyId: row.property_id,
        title: row.title,
        description: row.description,
        transactionType: row.transaction_type,
        price: Number(row.price),
        currency: row.currency,
        city: row.city,
        state: row.state,
        primaryAgentId: row.primary_agent_id,
        primaryAgentName: row.primary_agent_name,
        agencyName: row.agency_name,
        mediaPath: row.media_path,
        mediaCount: Number(row.media_count),
        isFavorite: row.is_favorite,
        outgoingMatchStatus: row.outgoing_match_status,
        incomingMatchStatus: row.incoming_match_status,
        hasActiveMatch: row.has_active_match,
      }));

      return {
        ok: true,
        page: {
          properties,
          totalCount:
            rows.length === 0 ? 0 : Number(rows[0]?.total_count ?? 0),
        },
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible conectar con Propiedades.',
      };
    }
  }

  async loadProfessionalProfile(
    agentId: string,
    signal?: AbortSignal,
  ): Promise<NetworkProfessionalProfileResult> {
    try {
      const row = await this.rpc<ProfessionalProfileRow>(
        'network_professional_profile_v1',
        { p_agent_id: agentId },
        signal,
      );

      if (row === null) {
        return {
          ok: false,
          message: 'No fue posible abrir este perfil profesional.',
        };
      }

      const profile: NetworkProfessionalProfile = {
        agentId: row.agent_id,
        publicName: row.public_name,
        licenseNumber: row.license_number,
        city: row.city,
        state: row.state,
        specialties: row.specialties,
        headline: row.headline,
        bio: row.bio,
        serviceAreas: row.service_areas,
        agencyName: row.agency_name,
        professionalRole: row.professional_role,
        inventory: row.inventory.map((listing) => ({
          listingId: listing.listing_id,
          title: listing.title,
          transactionType: listing.transaction_type,
          price: Number(listing.price),
          currency: listing.currency,
          propertyType: listing.property_type,
          city: listing.city,
          state: listing.state,
        })),
        blogPosts: row.blog_posts.map((post) => ({
          postId: post.post_id,
          title: post.title,
          subtitle: post.subtitle,
          body: post.body,
          publishedAt: post.published_at,
        })),
      };

      return { ok: true, profile };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible conectar con el perfil profesional.',
      };
    }
  }

  async loadCollections(
    signal?: AbortSignal,
  ): Promise<NetworkCollectionsResult> {
    try {
      const rows = await this.rpc<CollectionRow[]>(
        'network_collections_v1',
        {},
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer Colecciones.' };
      }

      const collections: NetworkCollection[] = rows.map((row) => ({
        collectionId: row.collection_id,
        name: row.name,
        visibility: row.visibility,
        workspaceId: row.workspace_id,
        itemCount: Number(row.item_count),
      }));

      return { ok: true, collections };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible leer Colecciones.' };
    }
  }

  async loadCollectionItems(
    kind: NetworkCollectionKind,
    signal?: AbortSignal,
  ): Promise<NetworkCollectionItemsResult> {
    if (kind === 'scans') return { ok: true, items: [] };

    try {
      const rows = await this.rpc<CollectionItemRow[]>(
        'network_collection_items_v1',
        { p_kind: kind, p_limit: 80 },
        signal,
      );

      if (rows === null) {
        return {
          ok: false,
          message: 'No fue posible leer esta colección.',
        };
      }

      const items: NetworkCollectionItem[] = rows.map((row) => ({
        itemType: row.item_type,
        itemId: row.item_id,
        agentId: row.agent_id,
        listingId: row.listing_id,
        title: row.title,
        subtitle: row.subtitle,
        city: row.city,
        state: row.state,
        agencyName: row.agency_name,
        relationshipLabel: row.relationship_label,
        mediaPath: row.media_path,
      }));

      return { ok: true, items };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible conectar con Colecciones.',
      };
    }
  }

  async loadCounts(signal?: AbortSignal): Promise<NetworkCountsResult> {
    try {
      const rows = await this.rpc<CountsRow[]>(
        'network_relationship_counts_v1',
        {},
        signal,
      );
      const row = rows?.[0];

      if (row === undefined) {
        return {
          ok: false,
          message: 'No fue posible leer el resumen de Network.',
        };
      }

      const counts: NetworkRelationshipCounts = {
        customers: Number(row.customers),
        owners: Number(row.owners),
        activeMatches: Number(row.active_matches),
        pendingOutgoing: Number(row.pending_outgoing),
        visibleDms: Number(row.visible_dms),
      };

      return { ok: true, counts };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible leer el resumen de Network.',
      };
    }
  }

  async markSeen(
    targetAgentId: string,
    seen: boolean,
    signal?: AbortSignal,
  ): Promise<NetworkMutationResult> {
    const result = await this.rpc<boolean>(
      'network_mark_seen_v1',
      { p_target_agent_id: targetAgentId, p_seen: seen },
      signal,
    );

    return result === null
      ? { ok: false, message: 'No fue posible guardar Visto.' }
      : { ok: true };
  }

  async loadMutualFollows(
    signal?: AbortSignal,
  ): Promise<NetworkConnectionsResult> {
    try {
      const rows = await this.rpc<ConnectionRow[]>(
        'network_mutual_follows_v1',
        {},
        signal,
      );

      if (rows === null) {
        return {
          ok: false,
          message: 'No fue posible leer tus seguidos mutuamente.',
        };
      }

      const connections: NetworkConnection[] = rows.map((row) => ({
        matchId: row.match_id,
        agentId: row.agent_id,
        publicName: row.public_name,
        headline: row.headline,
        city: row.city,
        state: row.state,
        specialties: row.specialties,
        agencyName: row.agency_name,
        professionalRole: row.professional_role,
        conversationId: row.conversation_id,
        matchedAt: row.matched_at,
      }));

      return { ok: true, connections };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return {
        ok: false,
        message: 'No fue posible conectar con tus seguidos mutuamente.',
      };
    }
  }

  async requestMatch(
    targetAgentId: string,
    signal?: AbortSignal,
  ): Promise<NetworkMatchRequestResult> {
    try {
      const result = await this.rpc<MatchStateRow>(
        'network_request_match_state_v1',
        { p_target_agent_id: targetAgentId },
        signal,
      );

      if (result === null) {
        return { ok: false, message: 'No fue posible solicitar Match.' };
      }

      return {
        ok: true,
        status: result.status,
        conversationId: result.conversation_id ?? null,
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible solicitar Match.' };
    }
  }

  async setFeedFavorite(
    listingId: string,
    favorite: boolean,
    signal?: AbortSignal,
  ): Promise<NetworkMutationResult> {
    try {
      const result = await this.rpc<boolean>(
        'inventory_set_favorite_v1',
        {
          p_listing_id: listingId,
          p_favorite: favorite,
        },
        signal,
      );

      return result === null
        ? { ok: false, message: 'No fue posible actualizar Favoritos.' }
        : { ok: true };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible actualizar Favoritos.' };
    }
  }

  async loadMessages(
    conversationId: string,
    signal?: AbortSignal,
  ): Promise<ConversationMessagesResult> {
    try {
      const rows = await this.rpc<MessageRow[]>(
        'conversation_messages_v1',
        {
          p_conversation_id: conversationId,
          p_before: null,
          p_limit: 60,
        },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer el chat.' };
      }

      const messages: ConversationMessage[] = rows
        .map((row) => ({
          messageId: row.message_id,
          body: row.body,
          messageType: row.message_type,
          createdAt: row.created_at,
          senderAgentId: row.sender_agent_id,
          senderName: row.sender_name,
          isMine: row.is_mine,
        }))
        .reverse();

      return { ok: true, messages };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible conectar con el chat.' };
    }
  }

  async sendMessage(
    conversationId: string,
    body: string,
    signal?: AbortSignal,
  ): Promise<SendMessageResult> {
    try {
      const messageId = await this.rpc<string>(
        'workspace_send_message_v1',
        { p_conversation_id: conversationId, p_body: body },
        signal,
      );

      return messageId === null
        ? { ok: false, message: 'No fue posible enviar el mensaje.' }
        : { ok: true, messageId };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { ok: false, message: 'No fue posible enviar el mensaje.' };
    }
  }
}
